import {
  ArrowLeft,
  Clipboard,
  FolderPlus,
  LayoutGrid,
  List,
  Loader2,
  RefreshCw,
  Scissors,
  Trash2,
  Upload,
} from "lucide-react";
import { ReactNode } from "react";

import { BusyAction } from "../types";

type MediaToolbarProps = {
  canGoBack: boolean;
  onBack: () => void;
  onRefresh: () => void;
  busyAction: BusyAction;
  selectedFolders: number;
  selectedAssets: number;
  onDelete: () => void;
  onMove: () => void;
  onPaste: () => void;
  hasPendingMove: boolean;
  onToggleViewMode: () => void;
  viewMode: "list" | "grid";
  onCreateFolder: () => void;
  onUploadClick: () => void;
  uploading: boolean;
  uploadProgress?: number | null;
  breadcrumbs?: ReactNode;
  showSelectionActions?: boolean;
  showCreateActions?: boolean;
  showUpload?: boolean;
};

export function MediaToolbar({
  canGoBack,
  onBack,
  onRefresh,
  busyAction,
  selectedFolders,
  selectedAssets,
  onDelete,
  onMove,
  onPaste,
  hasPendingMove,
  onToggleViewMode,
  viewMode,
  onCreateFolder,
  onUploadClick,
  uploading,
  uploadProgress,
  breadcrumbs,
  showSelectionActions = true,
  showCreateActions = true,
  showUpload = true,
}: MediaToolbarProps) {
  const hasSelection = selectedFolders > 0 || selectedAssets > 0;

  return (
    <div className="sticky top-0 z-10 flex h-10 flex-wrap items-center justify-between gap-2 border-b border-gray-300 bg-white">
      <div className="flex items-center gap-2 divide-x divide-gray-300">
        <button
          className="px-4 py-1 disabled:opacity-50"
          onClick={onBack}
          disabled={!canGoBack}
          title="Up"
        >
          <ArrowLeft size={16} />
        </button>
        <button className="py-1 pr-2" onClick={onRefresh} title="Refresh">
          <RefreshCw size={16} />
        </button>
        {showSelectionActions ? (
          <div className="flex items-center gap-0 px-0 lg:gap-2 lg:px-2">
            <button
              className="px-3 py-1 text-xs disabled:opacity-50"
              disabled={!hasSelection}
              title="Delete"
              onClick={onDelete}
            >
              <Trash2 size={16} />
            </button>
            <button
              className="px-3 py-1 text-xs disabled:opacity-50"
              disabled={!hasSelection}
              title="Move"
              onClick={onMove}
            >
              <Scissors size={16} />
            </button>
            <button
              className="px-3 py-1 text-xs disabled:opacity-50"
              disabled={!hasPendingMove}
              title="Paste"
              onClick={onPaste}
            >
              <Clipboard size={16} />
            </button>
          </div>
        ) : null}
        {breadcrumbs ? (
          <div className="hidden items-center gap-2 pl-3 lg:flex">
            {breadcrumbs}
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-0 lg:gap-2">
        {busyAction || uploading ? (
          <span className="text-pinkCustom flex items-center gap-1 rounded px-2 py-1 text-[11px]">
            <Loader2 size={14} className="animate-spin" />
            {uploading && uploadProgress !== null ? `${uploadProgress}%` : null}
          </span>
        ) : null}
        <button
          className="px-3 py-1 text-xs"
          title="Toggle layout"
          onClick={onToggleViewMode}
        >
          {viewMode === "list" ? <LayoutGrid size={16} /> : <List size={16} />}
        </button>
        {showCreateActions ? (
          <button
            className="px-3 py-1 text-xs"
            title="New folder"
            onClick={onCreateFolder}
          >
            <FolderPlus size={16} />
          </button>
        ) : null}
        {showUpload ? (
          <button
            className="px-3 py-1 text-xs disabled:opacity-50"
            title="Upload"
            disabled={uploading}
            onClick={onUploadClick}
          >
            <Upload size={16} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
